import React, { useEffect, useRef, useState } from 'react'
import { apiFetch } from './api'

export default function ReportsPage({ company, canApprove = true, isSupervisor = false }) {
  const [step, setStep] = useState(1)
  const [selectedSupervisor, setSelectedSupervisor] = useState(null)
  const [selectedRoute, setSelectedRoute] = useState(null)
  const [supervisors, setSupervisors] = useState([])
  const [routes, setRoutes] = useState([])
  const [routeSummaries, setRouteSummaries] = useState([])
  const [vendors, setVendors] = useState([])
  const [routeTotals, setRouteTotals] = useState({})
  const [supTotals, setSupTotals] = useState({})
  const [editVendor, setEditVendor] = useState(null)
  const [editForm, setEditForm] = useState({ returnCanGood: '', returnCanDamage: '', givenCanOld: '', givenCanNew: '', remark: '' })
  const [supView, setSupView] = useState('table')
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1)
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear())
    const [approvalSupervisorId, setApprovalSupervisorId] = useState(null)
    const [approveLoading, setApproveLoading] = useState(false)
    const [approveMessage, setApproveMessage] = useState('')
    const [approved, setApproved] = useState(false)
    const [approvalStatusLoading, setApprovalStatusLoading] = useState(false)
    const [approveAllLoading, setApproveAllLoading] = useState(false)
    const [approveAllMessage, setApproveAllMessage] = useState('')
    const [deleteAllLoading, setDeleteAllLoading] = useState(false)
    const [deleteAllMessage, setDeleteAllMessage] = useState('')
    const supTableRef = useRef(null)
    const routeTableRef = useRef(null)
    const vendorTableRef = useRef(null)
  const months = [
    { value: 1, label: 'Jan' }, { value: 2, label: 'Feb' }, { value: 3, label: 'Mar' }, { value: 4, label: 'Apr' },
    { value: 5, label: 'May' }, { value: 6, label: 'Jun' }, { value: 7, label: 'Jul' }, { value: 8, label: 'Aug' },
    { value: 9, label: 'Sep' }, { value: 10, label: 'Oct' }, { value: 11, label: 'Nov' }, { value: 12, label: 'Dec' },
  ]

  // fetch supervisors for company
  useEffect(() => {
    apiFetch(`/api/supervisors?company=${company}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(setSupervisors)
  }, [company])

  useEffect(() => {
    if (supervisors.length && !approvalSupervisorId) {
      setApprovalSupervisorId(supervisors[0].id)
    }
  }, [supervisors, approvalSupervisorId])

  // match selected month/year (allowing missing month/year to be excluded)
  const matchesPeriod = (item) => {
    const m = item?.month
    const y = item?.year
    if (!m || !y) return false
    return Number(m) === Number(selectedMonth) && Number(y) === Number(selectedYear)
  }

  const handleApprove = async () => {
    if (!approvalSupervisorId || !canApprove) return
    setApproveLoading(true)
    setApproveMessage('')
    const res = await apiFetch(`/api/supervisors/${approvalSupervisorId}/approve?month=${selectedMonth}&year=${selectedYear}`, {
      method: 'POST',
      credentials: 'include'
    })
    if (res.ok) {
      const data = await res.json().catch(() => ({}))
      setApproveMessage(`Approved and carried to ${selectedMonth === 12 ? 1 : selectedMonth + 1}/${selectedMonth === 12 ? selectedYear + 1 : selectedYear}. Created ${data.created ?? '0'} entries.`)
      setApproved(true)
    } else {
      const err = await res.json().catch(() => ({}))
      setApproveMessage(err.error || 'Approval failed')
      if ((err.error || '').toLowerCase().includes('already approved')) {
        setApproved(true)
      }
    }
    setApproveLoading(false)
  }

  const handleApproveAll = async () => {
    if (!canApprove || !supervisors.length) return
    setApproveAllLoading(true)
    setApproveAllMessage('')
    setDeleteAllMessage('')
    try {
      const res = await apiFetch(`/api/supervisors/approve/all?company=${encodeURIComponent(company)}&month=${selectedMonth}&year=${selectedYear}`, {
        method: 'POST',
        credentials: 'include'
      })
      if (res.ok) {
        const data = await res.json().catch(() => ({}))
        const msg = `Approved ${data.approved ?? 0}/${data.total ?? supervisors.length}. Already approved: ${data.alreadyApproved ?? 0}${data.failed ? `, Failed: ${data.failed}` : ''}.`
        setApproveAllMessage(msg)
        const approvedIds = data.approvedSupervisorIds || []
        if (approvedIds.includes(approvalSupervisorId)) {
          setApproved(true)
        } else {
          // fallback: recheck status for selected supervisor
          try {
            const statusRes = await apiFetch(`/api/supervisors/${approvalSupervisorId}/approve/status?month=${selectedMonth}&year=${selectedYear}`, { credentials: 'include' })
            if (statusRes.ok) {
              const status = await statusRes.json().catch(() => ({}))
              setApproved(Boolean(status.approved))
            }
          } catch (_) {
            /* ignore */
          }
        }
      } else {
        const err = await res.json().catch(() => ({}))
        setApproveAllMessage(err.error || 'Bulk approval failed')
      }
    } finally {
      setApproveAllLoading(false)
    }
  }

  const handleDeleteAllApprovals = async () => {
    if (!canApprove || !supervisors.length) return
    setDeleteAllLoading(true)
    setDeleteAllMessage('')
    setApproveAllMessage('')
    try {
      const res = await apiFetch(`/api/supervisors/approve/all/delete?company=${encodeURIComponent(company)}&month=${selectedMonth}&year=${selectedYear}`, {
        method: 'POST',
        credentials: 'include'
      })
      if (res.ok) {
        const data = await res.json().catch(() => ({}))
        const msg = `Deleted approvals for ${data.supervisorsDeleted ?? 0}/${data.totalSupervisors ?? supervisors.length} supervisors. Entries removed: ${data.totalEntriesDeleted ?? 0}${data.supervisorsFailed ? `, Failed: ${data.supervisorsFailed}` : ''}.`
        setDeleteAllMessage(msg)
        // refresh status for selected supervisor
        try {
          const statusRes = await apiFetch(`/api/supervisors/${approvalSupervisorId}/approve/status?month=${selectedMonth}&year=${selectedYear}`, { credentials: 'include' })
          if (statusRes.ok) {
            const status = await statusRes.json().catch(() => ({}))
            setApproved(Boolean(status.approved))
          } else {
            setApproved(false)
          }
        } catch (_) {
          setApproved(false)
        }
      } else {
        const err = await res.json().catch(() => ({}))
        setDeleteAllMessage(err.error || 'Bulk delete failed')
      }
    } finally {
      setDeleteAllLoading(false)
    }
  }

  // Fetch approval status so the button stays disabled across reloads
  useEffect(() => {
    const fetchStatus = async () => {
      if (!canApprove || !approvalSupervisorId) {
        setApproved(false)
        return
      }
      setApprovalStatusLoading(true)
      const res = await apiFetch(`/api/supervisors/${approvalSupervisorId}/approve/status?month=${selectedMonth}&year=${selectedYear}`, { credentials: 'include' })
      if (res.ok) {
        const data = await res.json().catch(() => ({}))
        setApproved(Boolean(data.approved))
      } else {
        setApproved(false)
      }
      setApprovalStatusLoading(false)
    }
    fetchStatus()
  }, [approvalSupervisorId, selectedMonth, selectedYear, canApprove])

  // Route and supervisor rollups via aggregated endpoint
  useEffect(() => {
    if (!supervisors.length) {
      setSupTotals({})
      setRouteTotals({})
      setRouteSummaries([])
      return
    }
    const fetchSummaries = async () => {
      const res = await apiFetch(`/api/routes/summary?company=${encodeURIComponent(company)}&month=${selectedMonth}&year=${selectedYear}`, { credentials: 'include' })
      if (!res.ok) {
        setSupTotals({})
        setRouteTotals({})
        setRouteSummaries([])
        return
      }
      const data = await res.json().catch(() => ({}))
      const summaries = data.routes || []
      setRouteSummaries(summaries)
      const rTotals = {}
      const sTotals = {}
      summaries.forEach(rt => {
        rTotals[rt.routeId] = {
          openingStock: rt.openingStock,
          returnCanGood: rt.returnCanGood,
          returnCanDamage: rt.returnCanDamage,
          givenCanOld: rt.givenCanOld,
          givenCanNew: rt.givenCanNew,
          closingStock: rt.closingStock,
          total: rt.total,
          oldGoodCanStock: rt.oldGoodCanStock,
        }
        const supId = rt.supervisorId
        if (!sTotals[supId]) {
          sTotals[supId] = { openingStock: 0, returnCanGood: 0, returnCanDamage: 0, givenCanOld: 0, givenCanNew: 0, closingStock: 0, total: 0, oldGoodCanStock: 0 }
        }
        sTotals[supId] = {
          openingStock: sTotals[supId].openingStock + (rt.openingStock || 0),
          returnCanGood: sTotals[supId].returnCanGood + (rt.returnCanGood || 0),
          returnCanDamage: sTotals[supId].returnCanDamage + (rt.returnCanDamage || 0),
          givenCanOld: sTotals[supId].givenCanOld + (rt.givenCanOld || 0),
          givenCanNew: sTotals[supId].givenCanNew + (rt.givenCanNew || 0),
          closingStock: sTotals[supId].closingStock + (rt.closingStock || 0),
          total: sTotals[supId].total + (rt.total || 0),
          oldGoodCanStock: sTotals[supId].oldGoodCanStock + (rt.oldGoodCanStock || 0),
        }
      })
      setRouteTotals(rTotals)
      setSupTotals(sTotals)
    }
    fetchSummaries()
  }, [supervisors, company, selectedMonth, selectedYear])

  // routes for selected supervisor (ensure list exists even if no vendors in period)
// routes for selected supervisor (ensure list exists even if no vendors in period)
// normalize so route.id === route.routeId when routeId exists (keeps compatibility)
useEffect(() => {
  if (step > 1 && selectedSupervisor) {
    apiFetch(`/api/routes`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(allRoutes => {
        const filtered = (allRoutes || []).filter(route => route.supervisor && route.supervisor.id === selectedSupervisor.id)
        const normalized = filtered.map(route => {
          // Store both: display routeId, keep original id for API
          return {
            ...route,
            displayRouteId: route.routeId ?? route.id,
            originalId: route.id
          }
        })
        setRoutes(normalized)
      })
  } else {
    setRoutes([])
  }
}, [step, selectedSupervisor])


  // vendors for selected route
  useEffect(() => {
    if (step === 3 && selectedRoute) {
      // Use originalId for API requests
      const routeApiId = selectedRoute.originalId || selectedRoute.id;
      apiFetch(`/api/vendors/route/${routeApiId}`, { credentials: 'include' })
        .then(r => r.ok ? r.json() : [])
        .then(vs => setVendors(vs.filter(matchesPeriod)))
    }
  }, [step, selectedRoute, selectedMonth, selectedYear])

  const openVendorEdit = v => {
    setEditVendor(v)
    setEditForm({
      returnCanGood: v.returnCanGood ?? 0,
      returnCanDamage: v.returnCanDamage ?? 0,
      givenCanOld: v.givenCanOld ?? 0,
      givenCanNew: v.givenCanNew ?? 0,
      remark: v.remark ?? '',
    })
  }

  const printTable = (ref, title = 'Table') => {
    if (!ref?.current) return
    const content = ref.current.innerHTML
    const printWindow = window.open('', '', 'width=900,height=700')
    if (!printWindow) return
    printWindow.document.write(`<!doctype html><html><head><title>${title}</title><style>body{font-family:Arial, sans-serif;padding:16px;}table{width:100%;border-collapse:collapse;}th,td{border:1px solid #d7dfe9;padding:8px;text-align:left;font-size:12px;}th{background:#f6f8fa;font-weight:700;}h3{margin-top:0;}</style></head><body><h3>${title}</h3>${content}</body></html>`)
    printWindow.document.close()
    printWindow.focus()
    printWindow.print()
    printWindow.close()
  }

  const handleVendorEditChange = e => {
    const { name, value } = e.target
    if (name === 'remark') {
      setEditForm(f => ({ ...f, remark: value }))
      return
    }
    const numVal = parseInt(value)
    const clamped = isNaN(numVal) ? 0 : Math.max(0, numVal)
    setEditForm(f => ({ ...f, [name]: clamped }))
  }

  const saveVendorEdit = async e => {
    e.preventDefault()
    if (!editVendor) return
    const payload = {
      ...editVendor,
      returnCanGood: parseInt(editForm.returnCanGood) || 0,
      returnCanDamage: parseInt(editForm.returnCanDamage) || 0,
      givenCanOld: parseInt(editForm.givenCanOld) || 0,
      givenCanNew: parseInt(editForm.givenCanNew) || 0,
      remark: editForm.remark,
    }
    const res = await apiFetch(`/api/vendors/${editVendor.id}/stock`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    })
    if (res.ok) {
      const updated = await res.json()
      setVendors(vs => {
        const next = vs.map(v => v.id === updated.id ? updated : v)
        const newRouteTotals = { ...routeTotals, [selectedRoute.id]: calcTotals(next) }
        setRouteTotals(newRouteTotals)
        if (selectedSupervisor) {
          const supRouteTotals = routes.filter(r => r.supervisor && r.supervisor.id === selectedSupervisor.id)
            .map(r => newRouteTotals[r.id] || {})
          const supAgg = calcTotals(supRouteTotals)
          setSupTotals(s => ({ ...s, [selectedSupervisor.id]: supAgg }))
        }
        return next
      })
      setEditVendor(null)
    }
  }

  const vendorEditModal = editVendor ? (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 30 }}>
      <div style={{ background: '#fff', padding: 20, borderRadius: 10, width: 360, boxShadow: '0 10px 30px rgba(0,0,0,0.12)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ fontWeight: 800 }}>Edit Stock Details</div>
          <button style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer' }} onClick={() => setEditVendor(null)}>×</button>
        </div>
        <form onSubmit={saveVendorEdit} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <label style={{ display: 'flex', flexDirection: 'column', fontSize: 13 }}>Return Can Good
            <input name="returnCanGood" value={editForm.returnCanGood} onChange={handleVendorEditChange} type="number" min="0" style={{ padding: 8, borderRadius: 6, border: '1px solid #d7dfe9' }} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', fontSize: 13 }}>Return Can Damage
            <input name="returnCanDamage" value={editForm.returnCanDamage} onChange={handleVendorEditChange} type="number" min="0" style={{ padding: 8, borderRadius: 6, border: '1px solid #d7dfe9' }} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', fontSize: 13 }}>Given Can Old
            <input name="givenCanOld" value={editForm.givenCanOld} onChange={handleVendorEditChange} type="number" min="0" style={{ padding: 8, borderRadius: 6, border: '1px solid #d7dfe9' }} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', fontSize: 13 }}>Given Can New
            <input name="givenCanNew" value={editForm.givenCanNew} onChange={handleVendorEditChange} type="number" min="0" style={{ padding: 8, borderRadius: 6, border: '1px solid #d7dfe9' }} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', fontSize: 13, gridColumn: '1 / -1' }}>Remark
            <input name="remark" value={editForm.remark} onChange={handleVendorEditChange} type="text" placeholder="Enter remark" style={{ padding: 8, borderRadius: 6, border: '1px solid #d7dfe9' }} />
          </label>
          <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
            <button type="button" className="btn secondary" onClick={() => setEditVendor(null)}>Cancel</button>
            <button type="submit" className="btn primary">Save changes</button>
          </div>
        </form>
      </div>
    </div>
  ) : null

  function calcTotals(items) {
    const sum = key => items.reduce((acc, v) => acc + (v[key] || 0), 0)
    const openingStock = sum('openingStock')
    const returnCanGood = sum('returnCanGood')
    const returnCanDamage = sum('returnCanDamage')
    const givenCanOld = sum('givenCanOld')
    const givenCanNew = sum('givenCanNew')
    const total = openingStock - returnCanGood - returnCanDamage
    const closingStock = items.reduce((acc, v) => {
      const itemTotal = (v.openingStock || 0) - (v.returnCanGood || 0) - (v.returnCanDamage || 0)
      return acc + itemTotal + (v.givenCanOld || 0) + (v.givenCanNew || 0)
    }, 0)
    return {
      openingStock,
      returnCanGood,
      returnCanDamage,
      givenCanOld,
      givenCanNew,
      closingStock,
      total,
      oldGoodCanStock: sum('oldGoodCanStock'),
    }
  }

  if (step === 1) {
    const supervisorRows = supervisors.map((sup, idx) => {
      const totals = supTotals[sup.id] || {}
      return (
        <tr key={sup.id} className="report-row" onClick={() => { setSelectedSupervisor(sup); setStep(2) }}>
          <td>{idx + 1}</td>
          <td style={{ color: '#2a5bd7', fontWeight: 700 }}>{sup.name}</td>
          <td>{totals.openingStock || 0}</td>
          <td>{totals.returnCanGood || 0}</td>
          <td>{totals.returnCanDamage || 0}</td>
          <td>{totals.total || 0}</td>
          <td>{totals.givenCanOld || 0}</td>
          <td>{totals.givenCanNew || 0}</td>
          <td>{totals.closingStock || 0}</td>
          <td>{totals.oldGoodCanStock || 0}</td>
        </tr>
      )
    })
    const chartData = supervisors.map(sup => {
      const totals = supTotals[sup.id] || {}
      return { id: sup.id, name: sup.name, opening: totals.openingStock || 0, closing: totals.closingStock || 0 }
    }).filter(d => d.opening || d.closing)
    const maxStock = chartData.length ? Math.max(...chartData.flatMap(d => [d.opening, d.closing])) || 1 : 1
    const supervisorTotals = calcTotals(Object.values(supTotals))
    const approveDisabled = !canApprove || approveLoading || approvalStatusLoading || !approvalSupervisorId || approved || approveAllLoading || deleteAllLoading
    const approveAllDisabled = !canApprove || approveAllLoading || supervisors.length === 0 || deleteAllLoading
    const deleteAllDisabled = !canApprove || deleteAllLoading || supervisors.length === 0 || approveAllLoading
    return (
      <div style={{ background: '#fff', padding: 18, borderRadius: 10, boxShadow: '0 4px 16px rgba(34,50,80,0.06)', maxWidth: 900 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 10 }}>
          <h2 style={{ fontSize: 24, margin: 0, flex: 1, textAlign: 'center' }}>Supervisors</h2>
          <button
            className="btn secondary"
            onClick={() => printTable(supTableRef, 'Supervisor Wise Stock')}
            disabled={supView !== 'table'}
            style={{ height: 34, background: '#2563eb', borderColor: '#2563eb', color: '#fff', opacity: supView === 'table' ? 1 : 0.5 }}
          >
            Print table
          </button>
        </div>
        <hr style={{ border: 0, borderTop: '1px solid #e5e7eb', margin: '0 0 10px' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 10 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#0f172a' }}>
              Month
              <select value={selectedMonth} onChange={e => setSelectedMonth(parseInt(e.target.value) || new Date().getMonth() + 1)} style={{ padding: 6, borderRadius: 6, border: '1px solid #d7dfe9' }}>
                {months.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#0f172a' }}>
              Year
              <input type="number" value={selectedYear} onChange={e => setSelectedYear(parseInt(e.target.value) || new Date().getFullYear())} style={{ padding: 6, borderRadius: 6, border: '1px solid #d7dfe9', width: 100 }} />
            </label>
            {canApprove && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#0f172a' }}>
                Supervisor
                <select value={approvalSupervisorId || ''} onChange={e => setApprovalSupervisorId(e.target.value ? parseInt(e.target.value) : null)} style={{ padding: 6, borderRadius: 6, border: '1px solid #d7dfe9', minWidth: 140 }}>
                  {supervisors.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </label>
            )}
            {canApprove && (
              <>
                <button type="button" className="btn primary" onClick={handleApprove} disabled={approveDisabled} style={{ height: 34, background: '#0f8a4d', borderColor: '#0f8a4d' }}>
                  {approveLoading ? 'Approving…' : 'Approve'}
                </button>
                {approveMessage && <span style={{ fontSize: 12, color: '#0f172a' }}>{approveMessage}</span>}
                <button type="button" className="btn secondary" onClick={handleApproveAll} disabled={approveAllDisabled} style={{ height: 34, background: '#0f172a', color: '#fff', borderColor: '#0f172a' }}>
                  {approveAllLoading ? 'Approving all…' : 'Approve all'}
                </button>
                {approveAllMessage && <span style={{ fontSize: 12, color: '#0f172a' }}>{approveAllMessage}</span>}
                <button type="button" className="btn secondary" onClick={handleDeleteAllApprovals} disabled={deleteAllDisabled} style={{ height: 34, background: '#b91c1c', color: '#fff', borderColor: '#b91c1c' }}>
                  {deleteAllLoading ? 'Deleting…' : 'Delete all approvals'}
                </button>
                {deleteAllMessage && <span style={{ fontSize: 12, color: '#0f172a' }}>{deleteAllMessage}</span>}
              </>
            )}
          </div>
        </div>
        <hr style={{ border: 0, borderTop: '1px solid #e5e7eb', margin: '10px 0 14px' }} />
        <div className="view-toggle" role="group" aria-label="Supervisor view toggle">
          <button
            type="button"
            className={`toggle-switch ${supView === 'chart' ? 'on' : 'off'}`}
            onClick={()=>setSupView(supView === 'table' ? 'chart' : 'table')}
            aria-pressed={supView === 'chart'}
            aria-label="Toggle between table and bar graph"
          >
            <span className="toggle-label left">Table View</span>
            <span className="toggle-label right">Graph View</span>
            <span className="toggle-thumb"></span>
          </button>
        </div>

        {supView === 'table' && (
          <div style={{ marginTop: 12 }}>
            <div style={{ maxHeight: 420, overflowY: 'auto' }} ref={supTableRef}>
              <table className="report-table" style={{ width: '100%' }}>
              <thead>
                <tr style={{ background: '#f6f8fa' }}>
                  <th>S No</th>
                  <th>Supervisor Name</th>
                  <th>Opening Stock</th>
                  <th>Return Can Good</th>
                  <th>Return Can Damage</th>
                  <th>Total</th>
                  <th>Given Can Old</th>
                  <th>Given Can New</th>
                  <th>CL. Stock</th>
                  <th>Old Good Can Stock</th>
                </tr>
              </thead>
              <tbody>
                {supervisorRows}
                {!isSupervisor && (
                  <tr style={{ fontWeight: 700, background: '#f6f8fa' }}>
                    <td colSpan={2}>TOTAL</td>
                    <td>{supervisorTotals.openingStock || 0}</td>
                    <td>{supervisorTotals.returnCanGood || 0}</td>
                    <td>{supervisorTotals.returnCanDamage || 0}</td>
                    <td>{supervisorTotals.total || 0}</td>
                    <td>{supervisorTotals.givenCanOld || 0}</td>
                    <td>{supervisorTotals.givenCanNew || 0}</td>
                    <td>{supervisorTotals.closingStock || 0}</td>
                    <td>{supervisorTotals.oldGoodCanStock || 0}</td>
                  </tr>
                )}
              </tbody>
              </table>
            </div>
          </div>
        )}

        {supView === 'chart' && (
          <div className="stock-chart-panel" style={{ maxHeight: 420, overflowY: 'auto', paddingRight: 6 }}>
            <div className="stock-chart-title">Opening vs Closing (Supervisor)</div>
            {chartData.length > 0 ? (
              <div className="stock-chart" aria-label="Opening versus closing stock by supervisor">
                <div className="stock-chart-scale" aria-hidden="true">
                  <div className="scale-pad"></div>
                  <div className="scale-line">
                    {[0,25,50,75,100].map(val => (
                      <div key={val} className="scale-tick" style={{ left: `${val}%` }}>
                        <span className="scale-label">{Math.round((maxStock * val) / 100)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="scale-pad"></div>
                </div>
                {chartData.map(d => (
                  <div className="stock-chart-row" key={d.id}>
                    <div className="stock-chart-label" title={d.name}>{d.name}</div>
                    <div className="stock-chart-bars" role="presentation">
                      <div className="stock-bar opening" style={{ width: `${(d.opening / maxStock) * 100}%` }} aria-label={`Opening stock ${d.opening}`}>
                        <span className="bar-value">Open {d.opening}</span>
                      </div>
                      <div className="stock-bar closing" style={{ width: `${(d.closing / maxStock) * 100}%` }} aria-label={`Closing stock ${d.closing}`}>
                        <span className="bar-value">Close {d.closing}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="stock-chart-placeholder" aria-label="No stock entries">
                <div className="stock-chart-label">No entries yet</div>
                <div className="stock-chart-bars" role="presentation">
                  <div className="stock-bar opening" style={{ width: '100%' }} aria-label="No data full green"></div>
                </div>
                <div className="stock-chart-values">
                  <span className="stock-pill opening">Full green</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    )
  }

  // Mapping (step 2) is accessible to both admin and operator for all companies
  if (step === 2 && selectedSupervisor) {
    const cellWrap = { wordBreak: 'break-word', whiteSpace: 'pre-wrap', maxWidth: 120 };
    const routeRows = routes.map((route, idx) => (
      <tr key={route.displayRouteId} className="report-row" onClick={() => { setSelectedRoute(route); setStep(3) }}>
        <td style={cellWrap}>{idx + 1}</td>
        <td style={cellWrap}>{route.displayRouteId || idx + 1}</td>
        <td style={{ ...cellWrap, color: '#2a5bd7', fontWeight: 700 }}>{route.name}</td>
        <td style={cellWrap}>{(routeTotals[route.originalId]?.openingStock) ?? 0}</td>
        <td style={cellWrap}>{(routeTotals[route.originalId]?.returnCanGood) ?? 0}</td>
        <td style={cellWrap}>{(routeTotals[route.originalId]?.returnCanDamage) ?? 0}</td>
        <td style={cellWrap}>{(routeTotals[route.originalId]?.total) ?? 0}</td>
        <td style={cellWrap}>{(routeTotals[route.originalId]?.givenCanOld) ?? 0}</td>
        <td style={cellWrap}>{(routeTotals[route.originalId]?.givenCanNew) ?? 0}</td>
        <td style={cellWrap}>{(routeTotals[route.originalId]?.closingStock) ?? 0}</td>
        <td style={cellWrap}>{(routeTotals[route.originalId]?.oldGoodCanStock) ?? 0}</td>
      </tr>
    ))
    return (
      <div style={{ background: '#fff', padding: 18, borderRadius: 10, boxShadow: '0 4px 16px rgba(34,50,80,0.06)', maxWidth: 900 }}>
        <h3>Route Wise Stock - {selectedSupervisor.name}</h3>
        <div style={{ marginTop: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
            <button className="btn secondary" onClick={() => printTable(routeTableRef, 'Route Wise Stock')}>Print table</button>
          </div>
          <div style={{ maxHeight: 420, overflowY: 'auto' }} ref={routeTableRef}>
            <table className="report-table" style={{ width: '100%' }}>
            <thead>
              <tr style={{ background: '#f6f8fa' }}>
                <th>S No</th>
                <th>Route No</th>
                <th>Route Name</th>
                <th>Opening Stock</th>
                <th>Return Can Good</th>
                <th>Return Can Damage</th>
                <th>Total</th>
                <th>Given Can Old</th>
                <th>Given Can New</th>
                <th>CL. Stock</th>
                <th>Old Good Can Stock</th>
              </tr>
            </thead>
            <tbody>
              {routeRows}
              <tr style={{ fontWeight: 700, background: '#f6f8fa' }}>
                <td colSpan={3}>TOTAL</td>
                <td>{calcTotals(routes.map(r => routeTotals[r.originalId] || {})).openingStock || 0}</td>
                <td>{calcTotals(routes.map(r => routeTotals[r.originalId] || {})).returnCanGood || 0}</td>
                <td>{calcTotals(routes.map(r => routeTotals[r.originalId] || {})).returnCanDamage || 0}</td>
                <td>{calcTotals(routes.map(r => routeTotals[r.originalId] || {})).total || 0}</td>
                <td>{calcTotals(routes.map(r => routeTotals[r.originalId] || {})).givenCanOld || 0}</td>
                <td>{calcTotals(routes.map(r => routeTotals[r.originalId] || {})).givenCanNew || 0}</td>
                <td>{calcTotals(routes.map(r => routeTotals[r.originalId] || {})).closingStock || 0}</td>
                <td>{calcTotals(routes.map(r => routeTotals[r.originalId] || {})).oldGoodCanStock || 0}</td>
              </tr>
            </tbody>
            </table>
          </div>
        </div>
        <button className="btn secondary" style={{ marginTop: 18 }} onClick={() => setStep(1)}>Back</button>
      </div>
    )
  }

  if (step === 3 && selectedRoute) {
    const cellWrap = { wordBreak: 'break-word', whiteSpace: 'pre-wrap', maxWidth: 120 };
    const vendorRows = vendors.map((v, idx) => {
      const total = (v.openingStock || 0) - (v.returnCanGood || 0) - (v.returnCanDamage || 0)
      const closingStock = total + (v.givenCanOld || 0) + (v.givenCanNew || 0)
      return (
        <tr key={v.id} className="report-row" onClick={() => openVendorEdit(v)}>
          <td style={cellWrap}>{idx + 1}</td>
          <td style={cellWrap}>{v.vendorCode}</td>
          <td style={cellWrap}>{v.name}</td>
          <td style={cellWrap}>{v.openingStock}</td>
          <td style={cellWrap}>{v.returnCanGood}</td>
          <td style={cellWrap}>{v.returnCanDamage}</td>
          <td style={cellWrap}>{total}</td>
          <td style={cellWrap}>{v.givenCanOld}</td>
          <td style={cellWrap}>{v.givenCanNew}</td>
          <td style={cellWrap}>{closingStock}</td>
          <td style={cellWrap}>{v.oldGoodCanStock || 0}</td>
          <td style={cellWrap}>{v.remark}</td>
        </tr>
      )
    })
    const totals = calcTotals(vendors)
    return (
      <div style={{ background: '#fff', padding: 18, borderRadius: 10, boxShadow: '0 4px 16px rgba(34,50,80,0.06)', maxWidth: 900 }}>
        <h3>Vendor Wise Stock - {selectedRoute.name}</h3>
        <div style={{ marginTop: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
            <button className="btn secondary" onClick={() => printTable(vendorTableRef, 'Vendor Wise Stock')}>Print table</button>
          </div>
          <div style={{ maxHeight: 420, overflowY: 'auto' }} ref={vendorTableRef}>
            <table className="report-table" style={{ width: '100%' }}>
            <thead>
              <tr style={{ background: '#f6f8fa' }}>
                <th>S No</th>
                <th>Vendor Code</th>
                <th>Vendor Name</th>
                <th>Opening Stock</th>
                <th>Return Can Good</th>
                <th>Return Can Damage</th>
                <th>Total</th>
                <th>Given Can Old</th>
                <th>Given Can New</th>
                <th>CL. Stock</th>
                <th>Old Good Can Stock</th>
                <th>Remark</th>
              </tr>
            </thead>
            <tbody>
              {vendorRows}
              <tr style={{ fontWeight: 700, background: '#f6f8fa' }}>
                <td colSpan={3}>TOTAL</td>
                <td>{totals.openingStock}</td>
                <td>{totals.returnCanGood}</td>
                <td>{totals.returnCanDamage}</td>
                <td>{totals.total}</td>
                <td>{totals.givenCanOld}</td>
                <td>{totals.givenCanNew}</td>
                <td>{totals.closingStock}</td>
                <td>{totals.oldGoodCanStock}</td>
                <td></td>
              </tr>
            </tbody>
            </table>
          </div>
        </div>
        <button className="btn secondary" style={{ marginTop: 18 }} onClick={() => setStep(2)}>Back</button>
        {vendorEditModal}
      </div>
    )
  }

  return null
}
